/**
 * Theme-aware palette for the LIVE board.
 *
 * The scene composer colours cards from `defaultTokens` — concrete hex, needed
 * for DOM-less SVG/PNG export. On screen we want the active `data-theme`
 * instead, so this resolves the same `Tokens` shape from the live CSS var ramps
 * (`--slate-*`, `--arch-*` in theme.css). Vars are stored as `"R G B"` triples;
 * we format them to hex so the scene's `alpha()` / `mix()` helpers (which parse
 * hex) keep working unchanged. Cached per `data-theme` value; the board
 * recomposes on the `archlang:themechange` event (see builtinWidgets), so the
 * next read sees the new theme.
 *
 * Headless callers (server, MCP, SVG export) never import this — they stay on
 * `defaultTokens`, so exported artifacts are deterministic regardless of any
 * viewer's theme.
 */
import { defaultTokens, mix } from "@archlang/scene";

type Tokens = ReturnType<typeof defaultTokens>;

/** scene palette field → theme.css var. Only colour fields; radius / spacing /
 *  font come from `defaultTokens`. Both shipped themes define these ramps, so
 *  the mapping is theme-agnostic. */
const VAR_MAP: ReadonlyArray<readonly [keyof Tokens, string]> = [
  ["bg", "--slate-900"],
  ["bgElev1", "--slate-800"],
  ["bgElev2", "--slate-700"],
  ["fg", "--slate-200"],
  ["fgMuted", "--slate-400"],
  ["fgSoft", "--slate-300"],
  ["border", "--slate-700"],
  ["codeBg", "--slate-950"],
  ["link", "--arch-400"],
  ["info", "--arch-400"],
];

function hexFromVar(cs: CSSStyleDeclaration, name: string): string | null {
  const raw = cs.getPropertyValue(name).trim(); // "30 31 34"
  const m = /^(\d+)\s+(\d+)\s+(\d+)$/.exec(raw);
  if (!m) return null;
  const h = (n: string) => Math.max(0, Math.min(255, Number(n))).toString(16).padStart(2, "0");
  return `#${h(m[1]!)}${h(m[2]!)}${h(m[3]!)}`;
}

let cachedTheme: string | null = null;
let cachedBase: Partial<Tokens> = {};
// Per-accent token cache. The theme base (from CSS vars) and the numeric fields
// (`defaultTokens`) are page-constant; only `accent`/`accentSoft` vary per node.
// So the WHOLE Tokens object for a given accent is invariant across every node
// and every board on the page — resolve it once and share the frozen instance.
// A palette has few distinct accents (default + per-aspect colours), so this
// stays small; it's cleared whenever the theme swaps (base colours change).
let tokenCache = new Map<string, Tokens>();

function themeBase(): Partial<Tokens> {
  if (typeof document === "undefined") return {};
  const theme = document.documentElement.getAttribute("data-theme") ?? "";
  if (theme === cachedTheme) return cachedBase;
  const cs = getComputedStyle(document.documentElement);
  const base: Record<string, string> = {};
  for (const [field, varName] of VAR_MAP) {
    const hex = hexFromVar(cs, varName);
    if (hex) base[field] = hex;
  }
  cachedTheme = theme;
  cachedBase = base as Partial<Tokens>;
  tokenCache = new Map(); // theme changed → previously-cached palettes are stale
  return cachedBase;
}

/**
 * Live-board palette for a node accent: `defaultTokens` (numeric fields +
 * fallbacks) overlaid with the active theme's colours, with `accentSoft`
 * re-derived over the themed background so the accent tint matches.
 *
 * Memoised per accent (invalidated on theme swap): the returned object is
 * page-constant for a given (theme, accent), so hundreds of nodes across many
 * boards share ONE instance instead of each allocating a fresh 28-field object
 * per render — the object churn a catalog of live embeds would otherwise pay.
 */
export function themeTokens(accent = "#34d399"): Tokens {
  const base = themeBase(); // refreshes cachedTheme + clears tokenCache on swap
  const hit = tokenCache.get(accent);
  if (hit) return hit;
  const d = defaultTokens(accent);
  const bg = base.bg ?? d.bg;
  const t: Tokens = { ...d, ...base, accent, accentSoft: mix(accent, bg, 0.67) };
  tokenCache.set(accent, t);
  return t;
}

/** Test seam: drop the per-accent + theme caches so a fresh `data-theme` in the
 *  same document is picked up deterministically. */
export function __resetThemeTokenCacheForTest(): void {
  cachedTheme = null;
  cachedBase = {};
  tokenCache = new Map();
}
